/* eslint-disable react-refresh/only-export-components */
// context/LanguageContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

type Language = "en" | "sw";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

// Translation dictionary
const translations = {
  en: {
    // Navigation
    home: "Home",
    about: "About",
    packages: "Packages",
    findJobs: "Find Jobs",
    community: "Community",
    helpCenter: "Help Center",
    faqs: "FAQs",
    support: "Support",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",

    // Footer content
    brandDescription:
      "FindWork is a local hiring platform connecting employers with ready-to-work people fast.",
    stayConnected: "Stay Connected",
    quickLinks: "Quick Links",
    resources: "Resources",
    getStarted: "Get Started",
    signIn: "Sign In",
    goToDashboard: "Go to Dashboard",
    backToTop: "Back to Top",
    copyright: "All rights reserved.",
    builtWithLove: "Built with ❤️ for job seekers in Kenya",

    // Contact
    location: "Nairobi, Kenya",
    phone: "+254 758337470",
    email: "findworkenquiries@gmail.com",

    //navbar
    tagline: "Find Your Dream Job",
    searchJobs: "Search jobs",
    toggleDarkMode: "Toggle dark mode",
    notifications: "Notifications",
    user: "User",
    userRole: "user",
    noEmail: "No email",
    dashboard: "Dashboard",
    profileSettings: "Profile & Settings",
    profile: "Profile",
    signOut: "Sign Out",
    login: "Login",
    // getStarted: 'Get Started',
    getStartedFree: "Get Started Free",
  },
  sw: {
    // Navigation
    home: "Nyumbani",
    about: "Kuhusu",
    packages: "Vifurushi",
    findJobs: "Tafuta Kazi",
    community: "Jumuiya",
    helpCenter: "Kituo cha Usaidizi",
    faqs: "Maswali Yanayoulizwa Sana",
    support: "Msaada",
    privacyPolicy: "Sera ya Faragha",
    termsOfService: "Vigezo cha Huduma",

    // Footer content
    brandDescription:
      "Mshika mikaka yako wa kuwaunganisha wataalamu wenye vipaji na fursa bora za kazi nchini Kenya na nje.",
    stayConnected: "Kaa Ukiunganishwa",
    quickLinks: "Viungo vya Haraka",
    resources: "Rasilimali",
    getStarted: "Anza Sasa",
    signIn: "Ingia",
    goToDashboard: "Nenda kwenye Dashibodi",
    backToTop: "Rudi Juu",
    copyright: "Haki zote zimehifadhiwa.",
    builtWithLove: "Imeundwa kwa ❤️ kwa watafutaji wa kazi Kenya",

    // Contact
    location: "Nairobi, Kenya",
    phone: "+254 700 000 000",
    email: "support@FindWork.com",

    // New navbar translations
    tagline: "Tafuta Kazi Ya Ndoto Zako",
    searchJobs: "Tafuta kazi",
    toggleDarkMode: "Badilisha hali ya giza",
    notifications: "Arifa",
    user: "Mtumiaji",
    userRole: "mtumiaji",
    noEmail: "Hakuna barua pepe",
    dashboard: "Dashibodi",
    profileSettings: "Wasifu na Mipangilio",
    profile: "Wasifu",
    signOut: "Toka",
    login: "Ingia",
    // getStarted: 'Anza Sasa',
    getStartedFree: "Anza Bila Malipo",
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("en");

  const t = (key: string): string => {
    return (
      translations[language][
        key as keyof (typeof translations)[typeof language]
      ] || key
    );
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
